import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PendingProFormaOrder {
  id: string;
  order_number: string;
  created_at: string;
  quoted_price: number | null;
  destination_company: string | null;
  destination_address: string | null;
  destination_postal_code: string | null;
  destination_city: string | null;
  destination_country: string | null;
  destination_contact_name: string | null;
  delivery_slot: string | null;
  pickup_date: string | null;
  dry_ice_kg: number | null;
  package_description: string | null;
  discount_amount: number | null;
  discount_value: number | null;
  discount_type: string | null;
  coupon_code: string | null;
  coupon_discount_percent: number | null;
  subscription_discount_percent: number | null;
  estimated_delivery_date: string | null;
  carrier: string | null;
  order_items?: Array<{
    description: string;
    quantity: number;
    unit_price: number | null;
    item_type: string;
  }>;
}

export interface PendingProFormaResult {
  proFormas: PendingProFormaOrder[];
  count: number;
  hasPending: boolean;
  isNewCustomer: boolean;
}

/**
 * Fetches all pro forma (status='awaiting_approval') orders for the current user
 * plus a flag indicating whether the user is "new" (has no other non-draft orders).
 * Used to drive the dedicated /dashboard/goedkeuren landing page.
 */
export function usePendingProForma() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["pending-pro-forma", user?.id],
    enabled: !!user,
    refetchInterval: 30000,
    queryFn: async (): Promise<PendingProFormaResult> => {
      if (!user) {
        return { proFormas: [], count: 0, hasPending: false, isNewCustomer: false };
      }

      const [proFormaRes, otherRes] = await Promise.all([
        supabase
          .from("orders")
          .select(
            "id, order_number, created_at, quoted_price, destination_company, destination_address, destination_postal_code, destination_city, destination_country, destination_contact_name, delivery_slot, pickup_date, dry_ice_kg, package_description, discount_amount, discount_value, discount_type, coupon_code, coupon_discount_percent, subscription_discount_percent, estimated_delivery_date, carrier, order_items(description, quantity, unit_price, item_type)"
          )
          .eq("user_id", user.id)
          .eq("status", "awaiting_approval")
          .order("created_at", { ascending: false }),
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id)
          .not("status", "in", '("draft","awaiting_approval")'),
      ]);

      const proFormas = (proFormaRes.data || []) as PendingProFormaOrder[];
      const otherCount = otherRes.count || 0;

      return {
        proFormas,
        count: proFormas.length,
        hasPending: proFormas.length > 0,
        isNewCustomer: otherCount === 0,
      };
    },
  });
}